import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import Navbar from "@/pages/home/components/Navbar";
import CTAFooterSection from "@/pages/home/components/CTAFooterSection";

export default function ConfirmAttendance() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const recusar = searchParams.get("recusar") === "true";

  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [error, setError] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [memberName, setMemberName] = useState("");

  useEffect(() => {
    async function handleConfirmation() {
      if (!registrationId || !token) {
        setError("Link inválido ou expirado.");
        setLoading(false);
        return;
      }

      try {
        const regId = Number(registrationId);
        if (Number.isNaN(regId)) {
          setError("Link inválido ou expirado.");
          setLoading(false);
          return;
        }

        const { data, error } = await supabase.rpc("confirm_attendance", {
          p_registration_id: regId,
          p_token: token,
          p_confirm: !recusar,
        });

        if (error || !data || data.status === "invalido") {
          setError("Link inválido, expirado ou esta confirmação já foi processada.");
          setLoading(false);
          return;
        }

        setEventTitle(data.evento || "o evento");
        setMemberName(data.name || "");

        if (data.confirmado) {
          setConfirmed(true);
        } else {
          setDeclined(true);
        }
      } catch (err: any) {
        console.error("Erro ao processar confirmação:", err);
        setError("Erro ao processar sua confirmação. Tente novamente.");
      }

      setLoading(false);
    }

    handleConfirmation();
  }, [registrationId, token, recusar]);

  if (loading) {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Processando sua confirmação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-beige">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-lg mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-10 text-center">
            {error ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                  <i className="ri-error-warning-line text-2xl text-red-500" />
                </div>
                <h2 className="font-display text-xl text-brown mb-3">Ops!</h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                  {error}
                </p>
                <a
                  href="/"
                  className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  Voltar ao início
                </a>
              </>
            ) : confirmed ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <i className="ri-check-line text-2xl text-green-600" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                  Presença confirmada!
                </h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-2">
                  {memberName ? `${memberName}, ` : ""}sua presença no evento <strong>{eventTitle}</strong> foi confirmada com sucesso.
                </p>
                <p className="font-body text-sm text-brown/60 leading-relaxed mb-6">
                  Estamos muito felizes em contar com você! Nos vemos lá.
                </p>
                <a
                  href="/"
                  className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  Voltar ao início
                </a>
              </>
            ) : declined ? (
              <>
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className="ri-calendar-close-line text-2xl text-amber-600" />
                </div>
                <h2 className="font-display text-xl md:text-2xl text-brown mb-3">
                  Tudo bem!
                </h2>
                <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
                  Entendemos que não será possível participar do evento <strong>{eventTitle}</strong>. Agradecemos por ter nos avisado — isso nos ajuda na organização. Em breve teremos novos encontros, fique de olho!
                </p>
                <a
                  href="/"
                  className="inline-block px-6 py-3 rounded-full bg-primary text-white font-body text-sm tracking-wide uppercase hover:bg-primary-dark transition-colors whitespace-nowrap"
                >
                  Voltar ao início
                </a>
              </>
            ) : null}
          </div>
        </div>
      </section>

      <CTAFooterSection />
    </div>
  );
}